import React, { useCallback, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage, useIntl } from "react-intl";
import SweetAlert from "sweetalert2";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import ContentWrapper from "../../../components/content-wrapper";
import { APP_COLORS } from "../../witadmin/Common/constants";
import { DateRange, ExperimentStatus, MetricRange } from "./domain/experiment.entity";
import { useExperiment } from "./application/hooks/useExperiment";
import { useExperimentMutations } from "./application/hooks/useExperimentMutations";
import { useExperimentResults } from "./application/hooks/useExperimentResults";
import { ExperimentGrpcRepository } from "./infrastructure/repositories/ExperimentGrpcRepository";
import { ResultsHeader } from "./components/ResultsHeader";
import { ResultsWarningsBanner } from "./components/ResultsWarningsBanner";
import { VerdictBanner } from "./components/VerdictBanner";
import { ScenarioComparisonCards } from "./components/ScenarioComparisonCards";
import { ExperimentHealthStrip } from "./components/ExperimentHealthStrip";
import { ExperimentExportButtons } from "./components/ExperimentExportButtons";
import { ExperimentEvolutionChart } from "./components/ExperimentEvolutionChart";
import { AllMetricsTable } from "./components/AllMetricsTable";
import { SegmentsTable } from "./components/SegmentsTable";
import { messages } from "./experiment.messages";

const experimentRepository = new ExperimentGrpcRepository();

// The results screen itself no longer carries a back button: the header owns a small text link,
// and the footer's duplicate is gone. This one survives for the not-found/error states, which
// render no header at all and would otherwise be a dead end.
// `variant="outline-secondary"` (the same variant the page's other buttons use — Pausar,
// Finalizar test) is what actually gives it the hover color-change behavior; a plain `<button>`
// with a `variant` prop is a no-op, since that's not a real HTML attribute.
function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline-secondary" onClick={onClick}>
      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
      <FormattedMessage {...messages.results.backToList} />
    </Button>
  );
}

export function ExperimentResultsPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const propertyInfo = useProperty();
  const intl = useIntl();

  // Resolved (Open Question 3, Business Rule 8): ExperimentResultsPage is the single owner of the
  // shared range/custom MetricRange state every date-scoped section below reads or drives.
  // The whole experiment, not a trailing week: the page's headline question is "what has this
  // test produced so far", and a 7-day window silently disagreed with the verdict, the gates and
  // the day counts, all of which are life-of-experiment figures.
  const [range, setRange] = useState<MetricRange>("FULL");
  const [custom, setCustom] = useState<DateRange | undefined>(undefined);

  const { data: experiment, isLoading, isError, error } = useExperiment(
    propertyInfo.ticker,
    id,
    experimentRepository
  );

  // The shell also calls useExperimentResults itself, to read the backend-authoritative
  // `activeDays` for the header's meta line plus the header's freshness/source chips and the
  // page-level partial-data banner. React Query de-dupes this against every section mounted with
  // the same {propertyTicker, id, range, custom} key — no extra network call is introduced.
  const { data: results } = useExperimentResults(
    propertyInfo.ticker,
    id,
    range,
    experimentRepository,
    custom
  );

  const { changeStatus, isChangingStatus } = useExperimentMutations(
    propertyInfo.ticker,
    experimentRepository
  );

  const handleGoBack = useCallback(() => {
    history.push(`/${propertyInfo.ticker}/orchestrator/experiments`);
  }, [history, propertyInfo.ticker]);

  // Business Rule 3: every lifecycle action requires confirmation before the mutation fires.
  // Business Rule 4: buttons are disabled via `busy={isChangingStatus}` — no local useState
  // pending-state workaround.
  const handlePauseClick = useCallback(async () => {
    if (!experiment) return;
    const result = await SweetAlert.fire({
      title: intl.formatMessage(messages.confirm.pauseTitle),
      text: intl.formatMessage(messages.confirm.pauseText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: APP_COLORS.primary,
      cancelButtonColor: APP_COLORS.secondary,
      confirmButtonText: intl.formatMessage(messages.confirm.accept),
      cancelButtonText: intl.formatMessage(messages.confirm.cancel),
    });
    if (!result.isConfirmed) return;
    try {
      await changeStatus({ id: experiment.id, status: ExperimentStatus.EXPERIMENT_STATUS_PAUSED });
    } catch (e) {
      console.error("Error pausing experiment", e);
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(messages.results.actionFailed),
      });
    }
  }, [experiment, changeStatus, intl]);

  const handleResumeClick = useCallback(async () => {
    if (!experiment) return;
    const result = await SweetAlert.fire({
      title: intl.formatMessage(messages.confirm.resumeTitle),
      text: intl.formatMessage(messages.confirm.resumeText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: APP_COLORS.primary,
      cancelButtonColor: APP_COLORS.secondary,
      confirmButtonText: intl.formatMessage(messages.confirm.accept),
      cancelButtonText: intl.formatMessage(messages.confirm.cancel),
    });
    if (!result.isConfirmed) return;
    try {
      await changeStatus({ id: experiment.id, status: ExperimentStatus.EXPERIMENT_STATUS_RUNNING });
    } catch (e) {
      console.error("Error resuming experiment", e);
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(messages.results.actionFailed),
      });
    }
  }, [experiment, changeStatus, intl]);

  const handleFinishClick = useCallback(async () => {
    if (!experiment) return;
    const result = await SweetAlert.fire({
      title: intl.formatMessage(messages.confirm.finishTitle),
      text: intl.formatMessage(messages.confirm.finishText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: APP_COLORS.primary,
      cancelButtonColor: APP_COLORS.secondary,
      confirmButtonText: intl.formatMessage(messages.confirm.accept),
      cancelButtonText: intl.formatMessage(messages.confirm.cancel),
    });
    if (!result.isConfirmed) return;
    try {
      await changeStatus({
        id: experiment.id,
        status: ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED,
      });
    } catch (e) {
      console.error("Error finishing experiment", e);
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(messages.results.actionFailed),
      });
    }
  }, [experiment, changeStatus, intl]);

  if (isLoading) {
    return (
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.results.title} />
        </SectionHeading>
        <Container className="p-4" fluid>
          <div>
            <FormattedMessage {...messages.results.loading} />
          </div>
        </Container>
      </ContentWrapper>
    );
  }
  if (isError || !experiment) {
    const isNotFound = error instanceof Error && error.message === "Experiment not found";
    return (
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.results.title} />
        </SectionHeading>
        <Container className="p-4" fluid>
          <div className="d-flex flex-column gap-3">
            <div>
              <FormattedMessage
                {...(isNotFound ? messages.results.notFoundTitle : messages.results.errorTitle)}
              />
            </div>
            <BackButton onClick={handleGoBack} />
          </div>
        </Container>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Container className="p-4" fluid style={{ maxWidth: "1200px" }}>
        <div className="d-flex flex-column gap-4">
          {/* Partial/degraded data is a page-level fact, so it sits above everything the user
              might otherwise read as real. Replaces the console.warn-only diagnostic this shell
              carried until now. */}
          <ResultsWarningsBanner partial={results?.partial} warnings={results?.warnings} />

          <ResultsHeader
            experiment={experiment}
            activeDays={results?.activeDays}
            minDays={results?.minDays}
            busy={isChangingStatus}
            onBackClick={handleGoBack}
            onPauseClick={handlePauseClick}
            onResumeClick={handleResumeClick}
            onFinishClick={handleFinishClick}
            dataUntil={results?.dataUntil}
            source={results?.source}
          />

          <VerdictBanner
            propertyTicker={propertyInfo.ticker}
            experimentId={id}
            range={range}
            custom={custom}
            repository={experimentRepository}
          />

          <ScenarioComparisonCards
            propertyTicker={propertyInfo.ticker}
            experimentId={id}
            range={range}
            custom={custom}
            repository={experimentRepository}
          />

          {/* Replaces <RunningDaysChip>: the day count now sits in the header's time line, and
              the four health readings answer "is this test running cleanly" in one row. */}
          <ExperimentHealthStrip
            propertyTicker={propertyInfo.ticker}
            experimentId={id}
            range={range}
            custom={custom}
            repository={experimentRepository}
          />

          <ExperimentEvolutionChart
            propertyTicker={propertyInfo.ticker}
            experiment={experiment}
            repository={experimentRepository}
            range={range}
            custom={custom}
            onRangeChange={setRange}
            onCustomChange={setCustom}
          />

          <SegmentsTable
            propertyTicker={propertyInfo.ticker}
            experimentId={id}
            range={range}
            custom={custom}
            repository={experimentRepository}
          />

          {/* The exports belong to the table they export, so they ride in its header row rather
              than in a footer bar of their own. Passed in as a node so AllMetricsTable stays free
              of the export stack's HTTP client and file-download plumbing. */}
          <AllMetricsTable
            propertyTicker={propertyInfo.ticker}
            experimentId={id}
            range={range}
            custom={custom}
            repository={experimentRepository}
            headerActions={
              <ExperimentExportButtons
                experiment={experiment}
                repository={experimentRepository}
                propertyTicker={propertyInfo.ticker}
              />
            }
          />
        </div>
      </Container>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(ExperimentResultsPage);
